import React, { useState, useEffect, useRef } from 'react';
import PastillePresence from '../common/PastillePresence';
import { usePresence } from '../../hooks/usePresence';
import { nomAffichageBoitier } from '../../utils/nomBoitier';
import { useParams, useNavigate } from 'react-router-dom';
import DiagnosticsPanel from '../common/DiagnosticsPanel';
import { 
  Box, 
  Typography, 
  Grid, 
  Paper, 
  Tab, 
  Tabs, 
  CircularProgress, 
  Alert,
  Card,
  CardContent,
  Divider,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  FormGroup,
  Chip,
  LinearProgress,
  Stack,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import MemoryIcon from '@mui/icons-material/Memory';
import WifiIcon from '@mui/icons-material/Wifi';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import SensorsIcon from '@mui/icons-material/Sensors';
import SensorsOffIcon from '@mui/icons-material/SensorsOff';
import StorageIcon from '@mui/icons-material/Storage';
import WarningIcon from '@mui/icons-material/Warning';
import WaterIcon from '@mui/icons-material/Water';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { apiService, BoitierStatus, DiagnosticResponse, SensorField, HistoryData, HistoryPoint } from '../../services/ApiService';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
} from 'chart.js';
import 'chart.js/auto';
import { format, subHours, parseISO, differenceInHours } from 'date-fns';
import { fr } from 'date-fns/locale';
import 'chartjs-adapter-date-fns';

// Enregistrer les composants nécessaires pour Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale
);

// Configure ChartJS pour nettoyer les charts précédents
ChartJS.defaults.responsive = true;
ChartJS.defaults.maintainAspectRatio = false;
ChartJS.defaults.plugins.legend.position = 'top';

// Types HistoryData et HistoryPoint importés depuis ApiService.ts

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// Composant TabPanel pour afficher le contenu d'un onglet
function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`bain-tabpanel-${index}`}
      aria-labelledby={`bain-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// Fonction pour calculer le statut des mesures basé sur les diagnostics ET les données
// Retourne: 'disabled' (gris), 'all_ok' (vert), 'partial' (orange), 'none' (rouge)
const getSensorsStatus = (mqttConnected: boolean, diagnostics: DiagnosticResponse | null, bainData?: BoitierStatus | null): 'disabled' | 'all_ok' | 'partial' | 'none' => {
  // Si MQTT non connecté, les mesures sont désactivées
  if (!mqttConnected) {
    return 'disabled';
  }
  
  // Essayer d'abord avec les diagnostics
  if (diagnostics?.last_diagnostic?.sensors) {
    const sensors = diagnostics.last_diagnostic.sensors;
    const sensorNames = Object.keys(sensors);
    
    if (sensorNames.length > 0) {
      let okCount = 0;
      
      for (const name of sensorNames) {
        const sensor = sensors[name];
        if (sensor.status === 0) {
          okCount++;
        }
      }
      
      if (okCount === sensorNames.length) {
        return 'all_ok';
      } else if (okCount > 0) {
        return 'partial';
      } else {
        return 'none';
      }
    }
  }
  
  // Fallback: utiliser last_reading et fields (cohérent avec BainCard)
  if (bainData) {
    const lastReading = bainData.last_reading;
    const fields = bainData.fields;
    
    if (!lastReading || !fields || fields.length === 0) return 'none';
    
    let hasValue = 0;
    for (const field of fields) {
      const value = lastReading[field.field_code];
      if (value !== null && value !== undefined) {
        hasValue++;
      }
    }
    
    if (hasValue === fields.length) return 'all_ok';
    if (hasValue > 0) return 'partial';
    return 'none';
  }
  
  // Ni diagnostics ni données
  return 'none';
};

// Mapping du statut vers les propriétés du Chip
const getSensorsChipProps = (status: 'disabled' | 'all_ok' | 'partial' | 'none') => {
  switch (status) {
    case 'all_ok':
      return { color: 'success' as const, icon: <SensorsIcon /> };
    case 'partial':
      return { color: 'warning' as const, icon: <SensorsIcon /> };
    case 'none':
      return { color: 'error' as const, icon: <SensorsOffIcon /> };
    case 'disabled':
    default:
      return { color: 'default' as const, icon: <SensorsOffIcon /> };
  }
};

// Options par défaut pour les graphiques (sans forcer l'unité de temps)
const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: {
    mode: 'index' as const,
    intersect: false,
  },
  plugins: {
    legend: {
      position: 'top' as const,
    },
    tooltip: {
      enabled: true,
    },
    // Forcer la destruction de l'instance de graphique précédente
    destroy: true,
  },
};

const ERREUR_STATUT = 'Impossible de récupérer les données du boîtier';
const ERREUR_HISTORIQUE = "Impossible de charger l'historique";

const BoitierDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { presenceDe, supervision } = usePresence();
  const navigate = useNavigate();
  const boitierId = parseInt(id || '0', 10);
  const [shouldRedirect, setShouldRedirect] = useState<string | null>(null);
  
  const [tabValue, setTabValue] = useState(0);
  const [bainData, setBainData] = useState<BoitierStatus | null>(null);
  const [historyData, setHistoryData] = useState<HistoryData | null>(null);
  const [diagnosticsData, setDiagnosticsData] = useState<DiagnosticResponse | null>(null);
  const [deviceFields, setDeviceFields] = useState<SensorField[]>([]); // Champs dynamiques
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [historyPeriod, setHistoryPeriod] = useState<string>('1h');
  const [useCustomDateRange, setUseCustomDateRange] = useState<boolean>(false);
  const [startDateTime, setStartDateTime] = useState<Date | null>(subHours(new Date(), 1));
  const [endDateTime, setEndDateTime] = useState<Date | null>(new Date());
  
  // Couleurs pour les graphiques dynamiques
  const chartColors = ['#ff9800', '#2196f3', '#4caf50', '#e91e63', '#9c27b0', '#00bcd4', '#ff5722', '#795548'];
  
  // Références pour les graphiques dynamiques
  const chartRefs = useRef<any[]>([]);

  // Changer d'onglet
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Charger le statut actuel du bain
  const loadBainStatus = async (silent: boolean = false) => {
    if (!boitierId) return;
    try {
      if (!silent) setLoading(true);
      
      // Récupérer les données du bain
      const data = await apiService.getBainStatus(boitierId);
      
      // Note: Le Dashboard route déjà vers le bon composant selon le type
      // Pas de redirection ici pour éviter le flash de graphiques
      
      setBainData(data);
      // Ne nettoyer que l'erreur dont ce chargeur est responsable : statut
      // et historique se chargent independamment, et un setError(null)
      // global effaçait l'echec de l'autre — la page affichait alors
      // « Aucune donnee disponible » au lieu de dire ce qui n'allait pas.
      setError((precedent) => (precedent && precedent.startsWith(ERREUR_STATUT) ? null : precedent));
    } catch (error) {
      console.error('Erreur lors du chargement des données du boîtier:', error);
      if (!silent) setError(ERREUR_STATUT);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Charger les diagnostics du bain
  const loadDiagnostics = async () => {
    if (!boitierId) return;
    try {
      const diagnostics = await apiService.getBainDiagnostics(boitierId);
      setDiagnosticsData(diagnostics);
    } catch (error) {
      console.error('Erreur lors du chargement des diagnostics:', error);
      // Ne pas mettre d'erreur globale, les diagnostics sont optionnels
    }
  };

  // Charger les champs dynamiques du device
  const loadDeviceFields = async () => {
    if (!boitierId) return;
    try {
      const fields = await apiService.getDeviceFields(boitierId);
      // Trier par display_order si disponible
      const sortedFields = fields.sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
      setDeviceFields(sortedFields);
    } catch (error) {
      console.error('Erreur lors du chargement des champs:', error);
      // Fallback vers les champs par défaut si erreur
      setDeviceFields([
        { id: 0, field_code: 'temp', field_name: 'Température', unit: '°C' },
        { id: 1, field_code: 'pH', field_name: 'pH', unit: '' },
        { id: 2, field_code: 'cond', field_name: 'Conductivité', unit: 'mS/cm' }
      ]);
    }
  };

  // Sauvegarder la position de scroll avant le rafraîchissement
  const [scrollPosition, setScrollPosition] = useState(0);

  useEffect(() => {
    loadDeviceFields(); // Charger les champs en premier
    loadBainStatus();
    loadDiagnostics();
    const intervalId = setInterval(() => {
      // Actualisation silencieuse sans scroll ni spinner
      loadBainStatus(true);
      loadDiagnostics();
    }, 5000); // Actualisation toutes les 5 secondes (capteurs publient toutes les 10s)
    
    return () => clearInterval(intervalId);
  }, [boitierId]);

  // Fonction pour charger l'historique des données
  const fetchHistory = async (silent: boolean = false) => {
    if (!boitierId) return;
    
    // Calculer la période d'historique
    let startDate;
    let endDate = new Date();
    
    if (useCustomDateRange && startDateTime && endDateTime) {
      startDate = startDateTime;
      endDate = endDateTime;
    } else {
      // Gestion spéciale pour la période '7d' (en jours)
      if (historyPeriod === '7d') {
        // 7 jours = 168 heures
        startDate = subHours(endDate, 168);
      } else {
        // Format normal 1h, 3h, 12h, 24h, 48h
        const hoursToSubtract = parseInt(historyPeriod, 10);
        startDate = subHours(endDate, hoursToSubtract);
      }
    }
    
    try {
      if (!silent) setLoading(true);
      
      // Utiliser apiService au lieu de fetch direct
      const data = await apiService.getBainHistory(boitierId, startDate, endDate);
      setHistoryData(data);
      setError((precedent) => (precedent && precedent.startsWith(ERREUR_HISTORIQUE) ? null : precedent));
    } catch (err: any) {
      console.error('Erreur lors de la récupération de l\'historique:', err);
      if (!silent) setError(`${ERREUR_HISTORIQUE}: ${err.message || 'Erreur inconnue'}`);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Rafraîchir l'historique quand les paramètres de filtrage changent
  useEffect(() => {
    fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boitierId, historyPeriod, useCustomDateRange]);
  
  // Rafraîchir automatiquement l'historique à intervalle régulier
  useEffect(() => {
    // Ne configurer l'actualisation automatique que si on n'utilise pas de plage personnalisée
    if (!useCustomDateRange) {
      const intervalId = setInterval(() => fetchHistory(true), 60000); // Actualisation silencieuse toutes les 60 secondes
      return () => clearInterval(intervalId);
    }
  }, [boitierId, historyPeriod, useCustomDateRange]);

  // Fonction utilitaire pour calculer la plage temporelle courante
  const computeCurrentRange = (): { start: Date; end: Date } => {
    let end = new Date();
    let start: Date;
    if (useCustomDateRange && startDateTime && endDateTime) {
      start = startDateTime;
      end = endDateTime;
    } else {
      if (historyPeriod === '7d') {
        start = subHours(end, 168);
      } else {
        const hoursToSubtract = parseInt(historyPeriod, 10);
        start = subHours(end, hoursToSubtract);
      }
    }
    return { start, end };
  };

  // Fonction pour exporter les données en CSV (RAW, sans agrégation)
  const exportToCsv = async () => {
    try {
      const { start, end } = computeCurrentRange();
      // Récupérer les données brutes directement depuis l'API
      const rawHistory = await apiService.getBainHistory(boitierId, start, end, '5m', true);

      // Déterminer dynamiquement les champs disponibles (exclure boitier_id)
      const fieldCodes = Object.keys(rawHistory).filter(
        (key) => key !== 'boitier_id' && Array.isArray((rawHistory as any)[key])
      );

      // Construire les en-têtes à partir des champs du device ou des clés brutes
      const fieldLabels: { [key: string]: string } = {};
      for (const code of fieldCodes) {
        const field = deviceFields.find((f) => f.field_code === code);
        fieldLabels[code] = field ? `${field.field_name}${field.unit ? ' (' + field.unit + ')' : ''}` : code;
      }

      let csvContent = 'Date,' + fieldCodes.map((c) => fieldLabels[c]).join(',') + '\n';

      // Aligner les séries par timestamp
      const dataByTimestamp = new Map<string, { [key: string]: number }>();
      for (const code of fieldCodes) {
        const points = (rawHistory as any)[code] as HistoryPoint[] | undefined;
        points?.forEach((point) => {
          const existing = dataByTimestamp.get(point.ts) || {};
          existing[code] = point.value;
          dataByTimestamp.set(point.ts, existing);
        });
      }

      const sortedTimestamps = Array.from(dataByTimestamp.keys()).sort();
      sortedTimestamps.forEach((ts) => {
        const data = dataByTimestamp.get(ts)!;
        const date = new Date(ts).toLocaleString('fr-FR');
        const values = fieldCodes.map((c) => data[c] !== undefined ? data[c].toFixed(2) : '');
        csvContent += `${date},${values.join(',')}\n`;
      });

      let fileName = `bain_${boitierId}_donnees`;
      if (useCustomDateRange && startDateTime && endDateTime) {
        const startStr = startDateTime.toLocaleDateString('fr-FR').replace(/\//g, '-');
        const endStr = endDateTime.toLocaleDateString('fr-FR').replace(/\//g, '-');
        fileName += `_${startStr}_a_${endStr}`;
      } else {
        fileName += `_${historyPeriod}`;
      }
      fileName += '_raw.csv';

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', fileName);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Erreur lors de l\'export CSV RAW:', err);
      setError("Impossible d'exporter le CSV brut");
    }
  };

  // Vérifier si les données de diagnostic sont obsolètes (> 10 minutes)
  const isDiagnosticsStale = (timestamp: string | undefined): boolean => {
    if (!timestamp) return true;
    const diagnosticTime = new Date(timestamp).getTime();
    const now = Date.now();
    const tenMinutes = 10 * 60 * 1000; // 10 minutes en ms
    return (now - diagnosticTime) > tenMinutes;
  };

  // Fonction pour convertir le statut en texte et couleur
  // Basé sur les constantes ESP32: STATUS_OK=0, STATUS_WARNING=1, STATUS_ERROR=2, STATUS_UNKNOWN=3
  // Si les données sont obsolètes, afficher "Données obsolètes"
  const getStatusInfo = (status: number, checkStale: boolean = false) => {
    // Si on vérifie la fraîcheur et que les données sont obsolètes
    if (checkStale && diagnosticsData?.last_diagnostic?.timestamp && isDiagnosticsStale(diagnosticsData.last_diagnostic.timestamp)) {
      return { text: 'Données obsolètes', color: 'default' as const };
    }
    switch (status) {
      case 0:
        return { text: 'Connecté', color: 'success' as const };
      case 1:
        return { text: 'Avertissement', color: 'warning' as const };
      case 2:
        return { text: 'Erreur', color: 'error' as const };
      case 3:
        return { text: 'Inconnu', color: 'default' as const };
      default:
        return { text: 'Déconnecté', color: 'error' as const };
    }
  };

  // Fonction pour formater la durée
  const formatUptime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) return `${hours}h ${minutes}m ${secs}s`;
    if (minutes > 0) return `${minutes}m ${secs}s`;
    return `${secs}s`;
  };

  // Fonction pour formater les octets
  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  
  // Fonction pour calculer le volume total à partir des points de débit
  const calculateVolume = (debitPoints: HistoryPoint[] | undefined): { volume: number; unit: string } => {
    if (!debitPoints || debitPoints.length < 2) {
      return { volume: 0, unit: 'L' };
    }
    
    let totalVolume = 0;
    
    for (let i = 1; i < debitPoints.length; i++) {
      const prevTime = new Date(debitPoints[i - 1].ts).getTime();
      const currTime = new Date(debitPoints[i].ts).getTime();
      const intervalMs = currTime - prevTime;
      const intervalMin = intervalMs / 60000;
      
      const avgDebit = (debitPoints[i - 1].value + debitPoints[i].value) / 2;
      totalVolume += avgDebit * intervalMin;
    }
    
    if (totalVolume >= 1000) {
      return { volume: totalVolume / 1000, unit: 'm³' };
    }
    return { volume: totalVolume, unit: 'L' };
  };

  // Vérifier si un champ est de type débit
  const isDebitField = (fieldCode: string): boolean => {
    return fieldCode.toLowerCase().includes('debit');
  };

  // Préparer les données pour les graphiques
  const prepareChartData = (dataPoints: HistoryPoint[], label: string, color: string) => {
    // Si les données sont vides ou nulles, retourner un jeu de données vide mais valide
    if (!dataPoints || dataPoints.length === 0) {
      return {
        labels: [],
        datasets: [
          {
            label,
            data: [],
            fill: false,
            borderColor: color,
            tension: 0.1,
          },
        ],
      };
    }
    
    // Trier les points par timestamp pour éviter les courbes qui retournent en arrière
    const sorted = [...dataPoints].sort((a, b) => new Date(a.ts).getTime() - new Date(b.ts).getTime());
    
    return {
      labels: sorted.map(point => new Date(point.ts)),
      datasets: [
        {
          label,
          data: sorted.map(point => point.value),
          fill: false,
          borderColor: color,
          backgroundColor: color,
          tension: 0.1,
          pointRadius: 3,
          pointHoverRadius: 5,
          borderWidth: 2,
        },
      ],
    };
  };

  // Si l'ID du boîtier n'est pas valide
  if (!boitierId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">ID de boîtier invalide</Alert>
      </Box>
    );
  }

  // Fonction pour formater le titre avec le vrai nom
  const getPageTitle = () => {
    if (bainData?.name) {
      return `Détails - ${nomAffichageBoitier({ boitier_id: boitierId, name: bainData.name, hostname: (bainData as any).hostname })}`;
    }
    return `Détails - Boîtier #${boitierId}`;
  };

  // Déterminer dynamiquement l'unité de temps pour l'axe X selon la période
  const getTimeUnit = (): 'minute' | 'hour' | 'day' => {
    if (useCustomDateRange && startDateTime && endDateTime) {
      const diffHours = differenceInHours(endDateTime, startDateTime);
      if (diffHours <= 3) return 'minute';
      if (diffHours <= 48) return 'hour';
      return 'day';
    }
    switch (historyPeriod) {
      case '1h':
      case '3h':
        return 'minute';
      case '12h':
      case '24h':
      case '48h':
        return 'hour';
      case '7d':
      default:
        return 'day';
    }
  };

  const getXAxisOptions = () => {
    const { start, end } = computeCurrentRange();
    return {
      type: 'time' as const,
      min: start.getTime(),
      max: end.getTime(),
      time: {
        unit: getTimeUnit(),
        tooltipFormat: 'dd/MM/yyyy HH:mm:ss',
        displayFormats: {
          millisecond: 'HH:mm:ss',
          second: 'HH:mm:ss',
          minute: 'HH:mm',
          hour: 'HH:mm',
          day: 'dd/MM',
        },
      },
      title: {
        display: true,
        text: useCustomDateRange ? 'Date/Heure' : 'Heure',
      },
    };
  };

  return (
    <Box sx={{ flexGrow: 1, p: 2 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        {getPageTitle()}
      </Typography>
      
      {/* Carte d'état actuel */}
      <Card sx={{ mb: 4 }}>
        {/* Informations du boîtier */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : bainData ? (
          <Grid container spacing={2} sx={{ p: 2 }}>
            {/* Indicateurs MQTT et Mesures */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Stack direction="row" spacing={0.5}>
                  <PastillePresence presence={presenceDe(boitierId)} supervision={supervision} />
                  <Chip
                    icon={bainData.mqtt_connected ? <WifiIcon /> : <WifiOffIcon />}
                    label="MQTT"
                    color={bainData.mqtt_connected ? "success" : "default"}
                    size="small"
                    sx={{ fontSize: '0.75rem' }}
                  />
                  {(() => {
                    const sensorsStatus = getSensorsStatus(bainData.mqtt_connected, diagnosticsData, bainData);
                    const chipProps = getSensorsChipProps(sensorsStatus);
                    return (
                      <Chip
                        icon={chipProps.icon}
                        label="Mesures"
                        color={chipProps.color}
                        size="small"
                        sx={{ fontSize: '0.75rem' }}
                      />
                    );
                  })()}
                </Stack>
                {bainData.last_seen && (
                  <Typography variant="body2" color="textSecondary">
                    Dernière mesure: {format(new Date(bainData.last_seen), 'dd/MM/yyyy HH:mm:ss', { locale: fr })}
                  </Typography>
                )}
              </Box>
            </Grid>
            
            {/* Valeurs actuelles - dynamiques selon les champs du device */}
            {deviceFields.map((field) => (
              <Grid item xs={12} sm={Math.min(4, Math.floor(12 / deviceFields.length))} key={field.field_code}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      {field.field_name}
                    </Typography>
                    <Typography variant="h4" color={
                      bainData.last_reading && bainData.last_reading[field.field_code] !== undefined && bainData.last_reading[field.field_code] !== null 
                        ? 'text.primary' 
                        : 'error.main'
                    }>
                      {bainData.last_reading && bainData.last_reading[field.field_code] !== undefined && bainData.last_reading[field.field_code] !== null 
                        ? `${bainData.last_reading[field.field_code]}${field.unit ? ` ${field.unit}` : ''}` 
                        : 'N/A'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
            
            
            {/* Alertes */}
            {bainData.alerts && bainData.alerts.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mb: 1 }}>Alertes</Typography>
                {bainData.alerts.map((alert: string, index: number) => (
                  <Alert key={index} severity="warning" sx={{ mb: 1 }}>
                    {alert}
                  </Alert>
                ))}
              </Grid>
            )}
          </Grid>
        ) : (
          <Typography>Aucune donnée disponible</Typography>
        )}
      </Card>

      {/* Onglets pour les différents types de graphiques */}
      <Paper sx={{ width: '100%' }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
          sx={{
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: '3px 3px 0 0'
            }
          }}
        >
          {/* Onglets dynamiques pour chaque champ */}
          {deviceFields.map((field) => (
            <Tab key={field.field_code} label={field.field_name.toUpperCase()} />
          ))}
          <Tab label="DIAGNOSTICS" />
        </Tabs>
        
        {/* Contrôles de période */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="body2">Sélection de la période</Typography>
            
            {/* Bouton d'export CSV */}
            {historyData && (
              <Button
                variant="outlined"
                color="primary"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={exportToCsv}
              >
                Exporter CSV
              </Button>
            )}
          </Box>
          <FormGroup sx={{ mb: 2 }}>
            <FormControlLabel 
              control={<Switch checked={useCustomDateRange} onChange={(e) => setUseCustomDateRange(e.target.checked)} />} 
              label="Plage de dates personnalisée" 
            />
          </FormGroup>
          
          {useCustomDateRange ? (
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={5}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="body2" gutterBottom>Du:</Typography>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
                    <DateTimePicker
                      value={startDateTime}
                      onChange={(value) => setStartDateTime(value as Date | null)}
                      renderInput={(params) => <TextField size="small" fullWidth {...params} />}
                    />
                  </LocalizationProvider>
                </Box>
              </Grid>
              <Grid item xs={12} md={5}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="body2" gutterBottom>Au:</Typography>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
                    <DateTimePicker
                      value={endDateTime}
                      onChange={(value) => setEndDateTime(value as Date | null)}
                      renderInput={(params) => <TextField size="small" fullWidth {...params} />}
                    />
                  </LocalizationProvider>
                </Box>
              </Grid>
              <Grid item xs={12} md={2}>
                <Button 
                  variant="contained" 
                  color="primary" 
                  sx={{ mt: 3 }}
                  onClick={() => {
                    // Refresh data with new date range
                    if (startDateTime && endDateTime) {
                      const hoursDiff = differenceInHours(endDateTime, startDateTime);
                      if (hoursDiff <= 0) {
                        setError("La date de fin doit être postérieure à la date de début");
                        return;
                      }
                      setError(null);
                      // Force refresh by updating state
                      setHistoryData(null);
                      // Create a temp copy to force the effect to run
                      const tempStart = new Date(startDateTime.getTime());
                      const tempEnd = new Date(endDateTime.getTime());
                      setStartDateTime(tempStart);
                      setEndDateTime(tempEnd);
                    }
                  }}
                >
                  Appliquer
                </Button>
              </Grid>
            </Grid>
          ) : (
            <>
              <Typography variant="body2" gutterBottom>Période rapide :</Typography>
              <Grid container spacing={1}>
                {['1h', '3h', '12h', '24h', '48h', '7d'].map((period) => (
                  <Grid item key={period}>
                    <Box
                      sx={{
                        px: 2,
                        py: 1,
                        borderRadius: 1,
                        cursor: 'pointer',
                        bgcolor: historyPeriod === period ? 'primary.main' : 'background.paper',
                        color: historyPeriod === period ? 'white' : 'text.primary',
                        '&:hover': {
                          bgcolor: historyPeriod === period ? 'primary.dark' : 'action.hover',
                        },
                      }}
                      onClick={() => setHistoryPeriod(period)}
                    >
                      {period}
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </>
          )}
        </Box>

        {/* Contenu des onglets */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ p: 3 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        ) : (
          <>
            {/* TabPanels dynamiques pour chaque champ */}
            {deviceFields.map((field, index) => (
              <TabPanel key={field.field_code} value={tabValue} index={index}>
                {/* Affichage du volume pour les champs de type débit */}
                {isDebitField(field.field_code) && (
                  <Paper 
                    elevation={2}
                    sx={{ 
                      mb: 2, 
                      p: 2, 
                      backgroundColor: '#e3f2fd',
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      border: '1px solid #90caf9'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <WaterIcon sx={{ mr: 1, color: '#1565c0' }} />
                      <Typography variant="body1" sx={{ color: '#1565c0', fontWeight: 500 }}>
                        Volume sur la période :
                      </Typography>
                    </Box>
                    <Typography variant="h5" sx={{ color: '#1565c0', fontWeight: 700 }}>
                      {calculateVolume((historyData?.[field.field_code] as HistoryPoint[]) || undefined).volume.toFixed(2)} {calculateVolume((historyData?.[field.field_code] as HistoryPoint[]) || undefined).unit}
                    </Typography>
                  </Paper>
                )}
                <Box sx={{ height: 400 }}>
                  <Line 
                    data={prepareChartData(
                      (historyData && historyData[field.field_code] as HistoryPoint[]) || [], 
                      `${field.field_name}${field.unit ? ` (${field.unit})` : ''}`, 
                      chartColors[index % chartColors.length]
                    )} 
                    options={{
                      ...chartOptions,
                      scales: {
                        x: getXAxisOptions(),
                        y: {
                          beginAtZero: true,
                          suggestedMin: 0,
                          suggestedMax: 1,
                          title: {
                            display: true,
                            text: `${field.field_name}${field.unit ? ` (${field.unit})` : ''}`,
                          }
                        }
                      }
                    }} 
                  />
                </Box>
              </TabPanel>
            ))}
            {/* TabPanel Diagnostics - dernier onglet */}
            <TabPanel value={tabValue} index={deviceFields.length}>
              <>
                  {diagnosticsData?.last_diagnostic?.timestamp && isDiagnosticsStale(diagnosticsData.last_diagnostic.timestamp) && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      Diagnostics anciens : les informations datent du {format(new Date(diagnosticsData.last_diagnostic.timestamp), 'dd/MM/yyyy à HH:mm:ss', { locale: fr })}.
                    </Alert>
                  )}
                  <DiagnosticsPanel diagnostics={diagnosticsData?.last_diagnostic || null} />
                </>
            </TabPanel>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default BoitierDetail;
